import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon, RotateCcwIcon } from 'lucide-react';

/**
 * Approved cat-and-water-glass vignette (v7 edit). Footage is used exactly
 * as supplied. The walk-through-spill segment (0–7.0 source seconds: walk,
 * eye contact, paw lift, pause, knock and spill) plays at 1.265×; the final
 * settled pose plays at 1.10× (both rates are 1.10× the previous 1.15 / 1.0
 * pacing, so relative timing is preserved).
 * Replace with self-hosted assets before production.
 */
const DESKTOP_MP4 = 'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/6b8ffb3e-579e-405e-93df-8a1cf2b06e95.mp4';
const MOBILE_MP4 = 'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/38d1e55a-0252-4c4d-b7d9-9fe64ec6c9ad.mp4';
const POSTER = 'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/3e58413b-9028-4e66-aa78-58c5041ce129.webp';
const VISIBILITY_THRESHOLD = 0.35;

/** The walk-through-spill segment ends here; only the settled pose follows. */
const ACTION_END_SECONDS = 7.0;
const ACTION_RATE = 1.265;
const SETTLED_RATE = 1.1;

/** 1.265× walk-through-spill, 1.10× settled pose. Only assigns when the rate changes. */
function syncPlaybackRate(video: HTMLVideoElement | null) {
  if (!video) return;
  const rate = video.currentTime < ACTION_END_SECONDS ? ACTION_RATE : SETTLED_RATE;
  if (video.playbackRate !== rate) video.playbackRate = rate;
}

/** Soft cream feathering at the scene edges — no visible card or border. */
const EDGE_MASK = 'linear-gradient(to right, transparent 0%, #000 5%, #000 95%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 6%, #000 94%, transparent 100%)';
const EDGE_STYLE: React.CSSProperties = {
  aspectRatio: '1440 / 618',
  maskImage: EDGE_MASK,
  WebkitMaskImage: EDGE_MASK,
  maskComposite: 'intersect',
  WebkitMaskComposite: 'source-in',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%'
};
type Status = 'idle' | 'playing' | 'paused' | 'ended' | 'failed';
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
function prefersSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as Navigator & {
    connection?: {
      saveData?: boolean;
    };
  }).connection;
  return Boolean(connection?.saveData);
}
export function HeroCatVideo() {
  const sceneRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPausedRef = useRef(false);
  const visibleRef = useRef(false);
  const pendingPlayRef = useRef(false);

  // One source only, chosen at mount from the initial viewport width.
  const [source] = useState<string>(() => typeof window !== 'undefined' && window.innerWidth <= 767 ? MOBILE_MP4 : DESKTOP_MP4);
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => prefersReducedMotion() || prefersSaveData());
  // Auto playback is allowed unless motion/data preferences say otherwise.
  // An explicit Play press opts the visitor in.
  const autoplayRef = useRef(!reducedMotion);
  const [activated, setActivated] = useState(!reducedMotion);
  const [status, setStatus] = useState<Status>('idle');
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      const reduce = query.matches || prefersSaveData();
      setReducedMotion(reduce);
      if (reduce) {
        autoplayRef.current = false;
        const video = videoRef.current;
        if (video && !video.paused) {
          video.pause();
          setStatus('paused');
        }
      }
    };
    if (query.addEventListener) query.addEventListener('change', onChange);else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);else query.removeListener(onChange);
    };
  }, []);
  const attemptPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !autoplayRef.current) return;
    if (userPausedRef.current || video.ended) return;
    syncPlaybackRate(video);
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.then(() => setStatus('playing')).catch(() => setStatus(current => current === 'ended' ? current : 'idle'));
    }
  }, []);

  // A Play press before the video existed: start it once mounted.
  useEffect(() => {
    if (!activated || !pendingPlayRef.current) return;
    pendingPlayRef.current = false;
    attemptPlay();
  }, [activated, attemptPlay]);

  // Play once when visible; pause when it leaves the viewport.
  useEffect(() => {
    const node = sceneRef.current;
    if (!node || !activated) return;
    if (typeof IntersectionObserver === 'undefined') {
      visibleRef.current = true;
      attemptPlay();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      const visible = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;
      visibleRef.current = visible;
      const video = videoRef.current;
      if (!video) return;
      if (visible) {
        attemptPlay();
      } else if (!video.paused) {
        video.pause();
        setStatus(current => current === 'ended' ? current : 'paused');
      }
    }, {
      threshold: [0, VISIBILITY_THRESHOLD, 0.6]
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [activated, attemptPlay]);

  // Pause with the document.
  useEffect(() => {
    if (!activated) return;
    const onVisibilityChange = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) {
        if (!video.paused) {
          video.pause();
          setStatus(current => current === 'ended' ? current : 'paused');
        }
      } else if (visibleRef.current) {
        attemptPlay();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [activated, attemptPlay]);
  const handleToggle = () => {
    autoplayRef.current = true;
    if (!activated) {
      userPausedRef.current = false;
      pendingPlayRef.current = true;
      setActivated(true);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (status === 'ended') {
      userPausedRef.current = false;
      video.currentTime = 0;
      syncPlaybackRate(video);
      void video.play().then(() => setStatus('playing')).catch(() => setStatus('idle'));
      return;
    }
    if (!video.paused) {
      userPausedRef.current = true;
      video.pause();
      setStatus('paused');
      return;
    }
    userPausedRef.current = false;
    syncPlaybackRate(video);
    void video.play().then(() => setStatus('playing')).catch(() => setStatus('idle'));
  };
  const controlLabel = status === 'ended' ? 'Replay cat animation' : status === 'playing' ? 'Pause cat animation' : status === 'paused' ? 'Resume cat animation' : 'Play cat animation';
  const ControlIcon = status === 'playing' ? PauseIcon : status === 'ended' ? RotateCcwIcon : PlayIcon;
  return <div className="w-full" data-id="element-55" data-mp="2i9l61igvvplx">
      <div ref={sceneRef} className="relative w-full" style={EDGE_STYLE} data-id="element-56" data-mp="2idimtxfzo52n">
        <img src={POSTER} alt="" aria-hidden="true" decoding="async" className="absolute inset-0 h-full w-full object-contain" data-id="element-57" data-mp="2dccu3zmbwht4" />
        {activated && status !== 'failed' ? <video ref={videoRef} src={source} poster={POSTER} muted playsInline preload="auto" aria-hidden="true" tabIndex={-1} onLoadedMetadata={() => syncPlaybackRate(videoRef.current)} onTimeUpdate={() => syncPlaybackRate(videoRef.current)} onPlay={() => {
        syncPlaybackRate(videoRef.current);
        setStatus('playing');
      }} onPause={() => {
        const video = videoRef.current;
        if (video && !video.ended) setStatus('paused');
      }} onEnded={() => setStatus('ended')} onError={() => setStatus('failed')} className="pointer-events-none absolute inset-0 h-full w-full object-contain" data-id="element-58" data-mp="7je7nbwvt7gj" /> : null}
      </div>

      {status !== 'failed' ? <div className="mt-2 flex justify-end" data-id="element-59" data-mp="1sh8c5jw547id">
          <button type="button" onClick={handleToggle} className="inline-flex min-h-[44px] items-center gap-2 rounded-[2px] px-3 text-[13px] font-medium text-ink/70 transition-colors duration-150 ease-out hover:text-ink" data-id="element-60" data-mp="1sijergdun97w">
            <ControlIcon className="h-4 w-4" aria-hidden="true" data-id="element-61" data-mp="22ly8na8ryjf7" />
            {controlLabel}
          </button>
        </div> : null}
    </div>;
}